import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Badge,
  ProgressBar,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  faFileExport,
  faPlus,
  faCircleCheck,
  faCircleXmark
} from '@fortawesome/free-solid-svg-icons';
import ProjectBobinStateTable from '../../components/tables/ProjectBobinStateTable';
import FilterTab from '../../components/common/FilterTab';
import AddPhaseDialog from '../../components/production/AddPhaseDialog';
import SearchBox from '../../components/common/SearchBox';
import { redisFetch } from '../../features/jobs/api/http';
import { exactLength } from '../../helpers/formatLength';
import FilterButtonGroup from '../../components/common/FilterButtonGroup';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { useBobinsState } from '../../features/bobins/hooks/useBobinsState';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PHASES_ORDER, PHASE_LABEL, phaseColor } from './ProjectJobsMonitor';
import MachineChips from '../../components/common/MachineChips';
import { normalizeMachines, machineLabel } from '../../helpers/phaseMachines';
import AddPhantomTokenModal from '../../components/modals/AddPhantomTokenModal';
import PhantomTokenDetailModal from '../../components/modals/PhantomTokenDetailModal';
import ProductColorDot from '../../components/common/ProductColorDot';
import { destinationBadge } from '../../helpers/orderDestination';
import TableExpandButton from '../../components/common/TableExpandButton';
// One bobbin under several products (merge-execution, partial fork). Same rule
// the machine-queue table uses, so the two screens cannot disagree about which
// drums are shared.
import { buildSharedTokenIndex, sharedPartners } from '../../helpers/sharedTokens';
import { activeFilters, CLEARED } from '../../helpers/activeFilters';
import ProductAllocationButton from '../../components/project-management/ProductAllocationButton';
// The other half of a transform, and the way to it. Shared with the Projects
// table so the two lists say the same thing the same way.
import ForkLink from '../../components/project-management/ForkLink';
// N launches of one cable as one product, and the way back. The modal opens with
// the endpoint's own dry run — nothing is written until that has been read.
import CombineProductsModal from '../../components/project-management/CombineProductsModal';
import {
  FULL_TABLE_ROUTES,
  FULL_TABLE_PAGE_SIZE,
  FULL_TABLE_PARENTS
} from '../full-table/routes';

// -------------------- helpers --------------------
function progressToPercent(p) {
  if (p == null) return 0;
  if (p > 0 && p <= 1) return Math.round(p * 100);
  return Math.round(p);
}

function normalizeStatus(status) {
  return String(status || 'PENDING').toUpperCase();
}

function isDoneStatusUI(status) {
  const s = normalizeStatus(status);
  return s === 'DONE' || s === 'FINISHED' || s === 'COMPLETED';
}

function matchStatusFilter(row, statusFilter) {
  if (!statusFilter || statusFilter === 'all') return true;

  const s = normalizeStatus(row.status);

  if (statusFilter === 'completed') return isDoneStatusUI(s);

  if (statusFilter === 'ongoing') {
    // ongoing = are progress, dar nu e done; CONSUMING tokens are ongoing
    if (s === 'CONSUMING') return true;
    const pct = progressToPercent(row.progress);
    return !isDoneStatusUI(s) && pct > 0 && pct < 100;
  }

  if (statusFilter === 'inactive') {
    // inactive = PENDING cu progress 0 (sau explicit INACTIVE dacă ai)
    // A queued-but-not-yet-made drum (status PLANNED) is the purest case of
    // inactive there is, and it used to fall through this filter — so filtering
    // for "Inactive" hid exactly the rows somebody was looking for.
    const pct = progressToPercent(row.progress);
    return (s === 'PENDING' && pct === 0) || s === 'INACTIVE' || s === 'PLANNED';
  }

  return true;
}

function matchNextPhaseFilter(row, nextPhaseFilter) {
  if (!nextPhaseFilter || nextPhaseFilter === 'all') return true;

  const prog = Number(row.programmedLength ?? 0);
  const avail = Number(row.availableLength ?? 0);

  if (nextPhaseFilter === 'programmed') return prog > 0;

  if (nextPhaseFilter === 'partial') {
    // partial = prog > 0 dar prog < available
    // (dacă available 0, nu are sens)
    return avail > 0 && prog > 0 && prog < avail;
  }

  if (nextPhaseFilter === 'none') return prog <= 0;

  return true;
}

/**
 * A finished drum or coil that has nothing left to do in production, and has
 * not left it either.
 *
 * This screen lists what is still IN production, and a terminal token drops off
 * it only once the warehouse takes it — a drum at reception, a coil when its
 * pallet is marked done. Until then it sits here reading DONE beside rows that
 * are genuinely waiting to be made, and the only difference is the Next column
 * being blank. That is what makes somebody launch it a second time.
 *
 * `isFinalDrum` / `isFinalCoil` come from the SERVER, which has the recipe.
 * This was first written against `hasNextPhase`, which is about planned TASKS:
 * a job built by factory sync has every phase marked COMPLETED, so nothing is
 * pending, and an insulated CYYF conductor with a rope and two sheaths still
 * ahead of it in `phasePlan` read "DONE · AWAITING RECEPTION".
 */
function exitBadge(t) {
  if (normalizeStatus(t?.status) !== 'DONE') return null;
  if (!(Number(t?.availableLength || 0) > 0)) return null;
  if (t?.isFinalCoil) return { label: 'DONE · AWAITING LABELS', type: 'info' };
  if (t?.isFinalDrum) return { label: 'DONE · AWAITING RECEPTION', type: 'info' };
  return null;
}

function tokenBadge(status, progress) {
  const pct = progressToPercent(progress);
  const s = normalizeStatus(status);

  if (s === 'PHANTOM') {
    return { label: 'PHANTOM', type: 'info' };
  }
  if (s === 'CONSUMING') {
    return { label: 'CONSUMING', type: 'primary' };
  }
  if (s === 'RESERVED') {
    return { label: 'RESERVED', type: 'warning' };
  }
  if (s === 'CONSUMED') {
    // Finished, like DONE, but with nothing left on the drum. Green would read
    // as "there is cable here" on a screen whose whole job is saying where the
    // cable is; the label carries the distinction, the colour carries the fact
    // that it is over.
    return { label: 'CONSUMED', type: 'info' };
  }
  // A token being made right now. It had no case at all, so it fell through to
  // the grey below — and ACTIVE is the busiest status on the floor, so the rows
  // that mattered most were the ones painted "nothing to see here".
  if (s === 'ACTIVE') {
    return { label: 'ACTIVE', type: 'primary' };
  }

  const done =
    s === 'DONE' || s === 'FINISHED' || s === 'COMPLETED' || pct >= 100;

  return {
    label: done ? 'DONE' : s,
    // Nothing grey. An unmapped status coming out amber is something to look
    // at; coming out grey it looks like a row that does not matter.
    type: done ? 'success' : 'warning'
  };
}

// Plan-ahead completeness: how much of a PLANNED (not-yet-produced) token's length
// already has its downstream phase scheduled (programmedLength) vs its total planned
// length. Replaces the flat grey "PLANNED" badge — which never reflected reality — with
// UNPLANNED (nothing scheduled) / PARTIALLY PLANNED / FULLY PLANNED. Soft phoenix colours.
function planAheadStatus(programmed, planned) {
  const prog = Math.max(0, Number(programmed || 0));
  const total = Math.max(0, Number(planned || 0));
  if (total <= 0 || prog <= 0) return { label: 'UNPLANNED', bg: 'danger' };
  if (prog >= total - 0.5) return { label: '✓ FULLY PLANNED', bg: 'success' };
  return { label: 'PARTIALLY PLANNED', bg: 'warning' };
}

/**
 * A drum that has been launched and queued on a machine but not made yet, and
 * which nothing follows — `final_product` / `coils_on_machine` routing, or
 * simply the last phase in the recipe. The drum IS the product.
 *
 * It needs its own badge because the plan-ahead one would be a lie: there is no
 * downstream phase to schedule, so `planAheadStatus` would paint it a red
 * UNPLANNED for a drum that is already as planned as it can ever be.
 *
 * Grey on purpose. The rest of this screen avoids grey because an UNMAPPED
 * status coming out grey reads as "a row that does not matter" — but "queued,
 * not started" is a mapped state, and not-started is exactly what grey says.
 */
const QUEUED_BADGE = {
  label: 'QUEUED · NOT MADE YET',
  bg: 'secondary'
};

function buildPhaseTabs(tokens = []) {
  const counts = {};
  for (const t of tokens || []) {
    const ph = t?.phase;
    if (!ph) continue;
    counts[ph] = (counts[ph] || 0) + 1;
  }

  const tabs = [{ label: 'All', value: 'all', count: (tokens || []).length }];

  for (const phase of PHASES_ORDER) {
    if (!counts[phase]) continue;
    tabs.push({
      label: PHASE_LABEL[phase] || phase,
      value: phase,
      count: counts[phase]
    });
  }

  const known = new Set(PHASES_ORDER);
  const extras = Object.keys(counts).filter(p => !known.has(p));
  extras.sort((a, b) => String(a).localeCompare(String(b)));
  for (const phase of extras) {
    tabs.push({
      label: PHASE_LABEL[phase] || phase,
      value: phase,
      count: counts[phase]
    });
  }

  return tabs;
}

function phaseIndex(p) {
  const idx = PHASES_ORDER.indexOf(p);
  return idx === -1 ? 999 : idx;
}

//  create stable color per groupKey (jobId::phase)
function hashToHue(str) {
  // simple stable hash -> 0..359
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) % 360;
  }
  return h;
}

// -------------------- grouping + styling flags --------------------
// `sharedIndex` is built from EVERY token, not from the filtered set: a drum's
// partner can be sitting behind a phase tab or a status filter, and it is still
// the same bobbin. Building it from what happens to be on screen would make the
// badge come and go as the planner filters.
function buildRowsWithGrouping(tokens = [], selectedPhase = 'all', sharedIndex = null) {
  const filtered =
    selectedPhase === 'all'
      ? tokens
      : tokens.filter(t => t.phase === selectedPhase);

  // sort so groups are contiguous: jobId -> phase order -> tokenId
  //
  // jobId DESCENDING. A jobId is a Mongo ObjectId, whose hex rises with time, so
  // this puts the newest launch at the top. It used to be ascending, which on a
  // floor with 23 live jobs and ten rows a page buried everything launched today
  // on page six — the planner who has just pressed Launch looks at page one, and
  // a product that is there but six pages down is indistinguishable from one
  // that never landed.
  const sorted = [...filtered].sort((a, b) => {
    const ja = String(a.jobId || '');
    const jb = String(b.jobId || '');
    if (ja !== jb) return jb.localeCompare(ja);

    const pa = phaseIndex(a.phase);
    const pb = phaseIndex(b.phase);
    if (pa !== pb) return pa - pb;

    return String(a.tokenId || '').localeCompare(String(b.tokenId || ''));
  });

  // 1) add groupKey + groupFirst
  let prevKey = null;
  const tmp = sorted.map(t => {
    const groupKey = `${t.jobId}::${t.phase}`;
    const groupFirst = groupKey !== prevKey;
    prevKey = groupKey;
    // console.log('T PROGRES ? ', t);
    const pct = progressToPercent(t.progress);
    // A terminal token that production has finished with says so, rather than
    // reading DONE like a drum that still has three phases ahead of it.
    // A terminal token that production has finished with says so, rather than
    // reading DONE like a drum that still has three phases ahead of it.
    const badge = exitBadge(t) || tokenBadge(t.status, t.progress);

    return {
      ...t,
      rowId: `${t.jobId}:${t.tokenId}`,
      statusRaw: normalizeStatus(t.status),
      statusBadge: badge,
      progressPct: pct,
      // the OTHER products holding this same physical bobbin, if any
      sharedWith: sharedIndex ? sharedPartners(sharedIndex, t.tokenId, t.jobId) : [],
      groupKey,
      groupFirst
    };
  });

  // 2) compute groupPos + groupContinue
  for (let i = 0; i < tmp.length; i++) {
    const cur = tmp[i];
    const prev = tmp[i - 1];
    const next = tmp[i + 1];

    const samePrev = prev && prev.groupKey === cur.groupKey;
    const sameNext = next && next.groupKey === cur.groupKey;

    let groupPos = 'single';
    if (!samePrev && sameNext) groupPos = 'first';
    else if (samePrev && sameNext) groupPos = 'middle';
    else if (samePrev && !sameNext) groupPos = 'last';

    cur.groupPos = groupPos;
    cur.groupContinue = samePrev;
  }

  // 3) assign groupIndex (zebra on groups)
  const groupIndexMap = new Map();
  let gi = 0;
  for (const r of tmp) {
    if (!groupIndexMap.has(r.groupKey)) {
      groupIndexMap.set(r.groupKey, gi++);
    }
    r.groupIndex = groupIndexMap.get(r.groupKey);
    r.groupHue = hashToHue(r.groupKey); // stable accent per group
  }

  return tmp;
}

// ✅ central styling for ALL cells in a row (so separators disappear)
function cellPropsForRow(row, extraClass = '') {
  const o = row?.original || {};
  const isContinue = !!o.groupContinue;

  // background: zebra by groupIndex (very subtle)
  // use CSS vars from theme (phoenix) + a tiny transparency
  const bg =
    o.groupIndex % 2 === 0
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(255,255,255,0.06)';

  // left accent border per groupKey (stable hue)
  const accent = `hsl(${o.groupHue || 210} 70% 55%)`;

  const style = {
    background: bg,
    // remove inner line between rows in same group
    ...(isContinue ? { borderTop: '0px solid transparent' } : {}),
    // accent “stripe” on every row cell so group reads as one block
    borderLeft: `4px solid ${accent}`
  };

  // padding: first row slightly bigger, follow-ups tighter (looks grouped)
  const basePy = isContinue ? 'py-2' : 'py-3';

  return {
    className: `white-space-nowrap ${basePy} ${extraClass}`.trim(),
    style
  };
}

function colorHex(c) {
  if (c === 'GREEN-YELLOW') return '#9acd32';
  if (c === 'BLUE') return '#2f7cff';
  if (c === 'BROWN') return '#8b5a2b';
  if (c === 'BLACK') return '#111111';
  if (c === 'GREY' || c === 'GRAY') return '#7a7a7a';
  if (c === 'WHITE') return '#e5e5e5';
  if (c === 'RED') return '#d9534f';
  if (c === 'YELLOW') return '#f0c400';
  if (c === 'GREEN') return '#2e8b57';
  if (c === 'ORANGE') return '#f39c12';
  return '#8892a0';
}
// Product label for the bobin/phantom table. Phantom stock rows have no real
// product — show the token spec dynamically: WIRE-TYPE, SECTION and COLOR,
// each included only when present (e.g. "MULTI-WIRE 16", "SINGLE-WIRE BLUE",
// "MULTI-WIRE 16 BLUE").
function formatBobinProduct(row) {
  if (!row?.isPhantom) return row?.productLabel || '—';

  const pd = row.phantomData || {};
  const wireType = pd.wireType || row.wireType || '';
  const section = pd.section ?? row.section ?? null;
  const color = row.color || pd.color || '';

  const parts = [
    wireType,
    section != null && section !== '' ? `${section}` : '',
    color
  ].filter(Boolean);

  return parts.join(' ') || '—';
}

// -------------------- columns --------------------
const projectBobinStateColumns = [
  {
    accessorKey: 'jobId',
    header: 'Job ID',
    cell: ({ row: { original } }) => {
      if (!original.groupFirst) return null;

      const id = original.jobId || '';
      const isPhantom = original?.isPhantom || original?.status === 'PHANTOM';

      // A job-owned phantom (one a launched product actually uses) carries a
      // jobId → show the product/job link so it's clear WHICH product consumes
      // the phantom. Only a pure stock phantom (no jobId) shows the placeholder.
      if (!id) {
        return (
          <span className="fw-bold fs-8 text-muted">
            {isPhantom ? 'Phantom' : '—'}
          </span>
        );
      }

      return (
        <Link
          to={`/apps/project-management/project-details/${original.jobId}`}
          className="text-decoration-none fw-bold fs-8"
          title={original.jobId}
        >
          {id.slice(0, 8).toUpperCase()}…
        </Link>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'align-top'),
      headerProps: { style: { width: '10%' } }
    }
  },

  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => {
      // Phantom rows are individual stock items (often jobId=null → all in one
      // group), so always show their own label. Real bobins keep the group-header
      // behaviour (label only on the first row of the job/phase group).
      if (!original.isPhantom && !original.groupFirst) return null;

      return (
        <div
          className="d-flex flex-column justify-content-center"
          style={{ minHeight: 32 }}
        >
          <span className="fw-semibold d-inline-flex align-items-center gap-2">
            <ProductColorDot row={original} />
            {formatBobinProduct(original)}
          </span>
          {/* Only on the group header, like the label itself — repeating it on
              every drum of the product would be noise. */}
          <ForkLink job={original} className="mt-1" />
        </div>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'align-top fw-semibold'),
      headerProps: { style: { width: '10%' } }
    }
  },

  {
    accessorKey: 'phase',
    header: 'Phase',
    cell: ({ row: { original } }) => {
      const label = PHASE_LABEL[original.phase] || original.phase || '—';
      // Unwinding drums are wire-Ø specific (unwind Ø1.70 vs Ø2.14) — surface the
      // wire diameter so the operator can tell them apart at a glance.
      const showDia =
        original.phase === 'unwinding' && original.wireDiameter != null;
      const hue = phaseColor(original.phase);

      // AND WHICH MACHINE MADE IT. A token names no machine — the task that
      // lists it in `outputs` does — so nothing on this row could answer the
      // question every row raises: where did this drum come from, and is that
      // line still turning. `controller/bobins.js` now says, for every token
      // rather than only for coils.
      const made = original.producedByMachine
        ? [
            {
              machineId: String(original.producedByMachine),
              label: machineLabel(original.producedByMachine),
              state: original.producedByRunning ? 'running' : 'done',
              count: 1,
              plannedLength: 0
            }
          ]
        : [];

      return (
        <div className="d-flex flex-column gap-1">
          <span className="d-inline-flex align-items-center gap-2">
            {/* The phase, in its own colour — the same hue the Project Monitor
                bar uses for it, so the two screens name it the same way. A
                left bar rather than a filled pill: the row already carries
                badges for status and destination, and a third filled chip made
                the line read as three competing labels. */}
            <span
              className="fw-semibold"
              style={{
                borderLeft: `3px solid ${hue}`,
                paddingLeft: 7,
                color: hue
              }}
            >
              {label}
            </span>
            {showDia && (
              <Badge variant="phoenix" bg="info">
                Ø{original.wireDiameter}
              </Badge>
            )}
          </span>

          {made.length > 0 && (
            <MachineChips machines={made} phaseLabel={label} />
          )}
        </div>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row),
      headerProps: { style: { width: '12%' } }
    }
  },

  {
    id: 'status',
    header: 'Status',
    accessorFn: row => row.statusRaw,
    cell: ({ row: { original } }) => {
      // PLAN-AHEAD tokens (planned, not produced): show downstream planning completeness
      // instead of the flat "PLANNED" badge.
      if (original.isPlanned) {
        const ps = original.isTerminalPlan
          ? QUEUED_BADGE
          : planAheadStatus(original.programmedLength, original.plannedLength);
        return (
          <Badge
            variant="phoenix"
            bg={ps.bg}
            title={
              original.isTerminalPlan
                ? 'Launched and queued on the machine. Nothing follows this drum — it is the finished product.'
                : undefined
            }
          >
            {ps.label}
          </Badge>
        );
      }
      const b = original.statusBadge || { label: 'PENDING', type: 'warning' };
      return (
        <Badge variant="phoenix" bg={b.type}>
          {String(b.label || '').toUpperCase()}
        </Badge>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-center'),
      headerProps: { style: { width: '10%' }, className: 'text-center' }
    }
  },

  {
    accessorKey: 'availableLength',
    header: 'Available (m)',
    cell: ({ row: { original } }) => {
      // A queued drum holds nothing yet, so "0.00" is true but useless — the
      // number worth reading is how long it is MEANT to be. Said as a plan, in
      // muted type, so it cannot be mistaken for cable that exists.
      if (original.isPlanned && original.isTerminalPlan) {
        return (
          <span
            className="text-body-tertiary fst-italic"
            title="Planned length — nothing has been produced on this drum yet"
          >
            {Number(original.plannedLength ?? 0).toFixed(2)} plan
          </span>
        );
      }
      return original.availableLength != null
        ? Number(original.availableLength).toFixed(2)
        : '-';
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-end align-top'),
      headerProps: { style: { width: '10%' }, className: 'text-end' }
    }
  },

  {
    accessorKey: 'producedLength',
    header: 'Produced (m)',
    // A corrected length reads exactly like a measured one, and the difference
    // matters: 4.29 m off a machine is a drum change, 4.29 m typed by somebody
    // is a decision. The drum carries its own correction history (written by
    // redis_dev/helpers/tokenCorrection.js), so say so where the number is read.
    cell: ({ row: { original } }) => {
      const corrections = Array.isArray(original?.corrections)
        ? original.corrections
        : [];
      const last = corrections[corrections.length - 1];
      return (
        <span
          title={
            last
              ? corrections
                  .map(
                    c =>
                      `${new Date(Number(c.ts) || 0).toLocaleString('ro-RO')} · ` +
                      `${Number(c.from || 0).toFixed(2)} → ${Number(c.to || 0).toFixed(2)} m · ` +
                      `${c.by || 'System'}${c.reason ? ` · ${c.reason}` : ''}`
                  )
                  .join('\n')
              : undefined
          }
        >
          {Number(original.producedLength ?? 0).toFixed(2)}
          {last ? (
            <span className="text-warning ms-1" aria-label="corrected">
              ✎
            </span>
          ) : null}
        </span>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-end align-top'),
      headerProps: { style: { width: '10%' }, className: 'text-end' }
    }
  },

  {
    accessorKey: 'tokenId',
    header: 'Token',
    cell: ({ row: { original } }) => {
      const dotColor = colorHex(original?.color || original?.color);
      const isPhantom = original?.isPhantom || original?.status === 'PHANTOM';
      // One bobbin listed under several products — the same drum appears in
      // each of their rows, and without this the only way to notice was to read
      // token ids down the table and spot a repeat.
      const shared = original?.sharedWith || [];

      // Deep-link to Factory 3D — locate this token physically on the floor.
      const factory3dHref = `/apps/project-management/factory-3d?token=${encodeURIComponent(
        `${original.jobId}:${original.tokenId}`
      )}&phase=${encodeURIComponent(original.phase || '')}`;

      return (
        <span
          className={`px-2 py-1 rounded-2 d-inline-block ${isPhantom ? 'phantom-token-link' : ''}`}
          style={{
            fontFamily: 'monospace',
            cursor: isPhantom ? 'pointer' : 'default',
            // A dashed outline so a shared drum is findable by scanning the
            // column, before reading a single word of it.
            ...(shared.length
              ? {
                  border: '1px dashed var(--phoenix-warning)',
                  background: 'rgba(255,193,7,0.08)'
                }
              : {})
          }}
          title={original.tokenId}
          data-phantom-id={isPhantom ? original.tokenId : undefined}
        >
          {isPhantom && (
            <Badge bg="info" className="me-1" style={{ fontSize: 9 }}>PH</Badge>
          )}
          {/* tokenId → Factory 3D (stopPropagation so it doesn't trigger the
              phantom-source reveal on the PH badge / row) */}
          <Link
            to={factory3dHref}
            onClick={e => e.stopPropagation()}
            className="text-decoration-none fw-semibold"
            title="Vezi în Factory 3D"
            style={{ color: 'inherit', borderBottom: '1px dotted currentColor' }}
          >
            {original.tokenId}
          </Link>
          {dotColor ? (
            <span
              style={{
                marginLeft: '4px',
                width: 12,
                height: 12,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.15)',
                background: dotColor,
                display: 'inline-block'
              }}
            />
          ) : null}
          <Link
            to={factory3dHref}
            onClick={e => e.stopPropagation()}
            title="Vezi în Factory 3D"
            className="ms-1 align-middle text-decoration-none"
          >
            <Badge bg="primary" style={{ fontSize: 8 }}>3D</Badge>
          </Link>

          {shared.length > 0 && (
            <Badge
              bg="warning"
              className="ms-1 align-middle"
              style={{ fontSize: 8 }}
              title={`One physical bobbin, shared with: ${shared.join(', ')}. The same drum is listed under each of them — do not count it twice.`}
            >
              ⇄ {shared.length + 1} products
            </Badge>
          )}
        </span>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row),
      headerProps: { style: { width: '14%' } }
    }
  },

  {
    accessorKey: 'progressPct',
    header: 'Progress',
    cell: ({ row: { original } }) => {
      // console.log(original);
      return <ProgressCell pct={original.progressPct ?? 0} />;
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-end align-top'),
      headerProps: { style: { width: '14%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'hasNextPhase',
    header: 'Next',
    cell: ({ row: { original } }) => {
      const ok = !!original.hasNextPhase;

      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {ok
                ? 'Token is used in a downstream task'
                : 'Token has NO downstream task (stops here)'}
            </Tooltip>
          }
        >
          <span className="d-inline-flex align-items-center justify-content-center">
            <FontAwesomeIcon
              icon={ok ? faCircleCheck : faCircleXmark}
              className={ok ? 'text-success' : 'text-danger'}
              style={{ fontSize: 18 }}
            />
          </span>
        </OverlayTrigger>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-center'),
      headerProps: { style: { width: '6%' }, className: 'text-center' }
    }
  },
  {
    accessorKey: 'programmedLength',
    header: 'Prog',
    cell: ({ row: { original } }) => (
      <ProgrammedCell
        programmedLength={original.programmedLength}
        availableLength={original.availableLength}
        nextMachines={original.nextMachines}
        nextPhaseName={original.nextPhaseName}
      />
    ),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-end align-top'),
      headerProps: { style: { width: '14%' }, className: 'text-end' }
    }
  }
];

function progressVariant(pct) {
  // 0-19: danger, 20-59: warning, 60-89: info, 90-100: success
  if (pct < 20) return 'danger';
  if (pct < 60) return 'warning';
  if (pct < 90) return 'info';
  return 'success';
}

// The two bar columns — Progress and PROG — sit three columns apart and are
// read across. They had different bar heights (10 vs 8) and different trailing
// label widths (42 vs 60), and once PROG grew a row of machine chips the
// middle-aligned cell pushed its bar UP off the other's axis. Reported
// 2026-10-29: "acceasi problema cu progress barul din coloana progress si
// progress barul din coloana prog."
//
// One geometry for both, stated here: the bar is the FIRST row of the cell, so
// anything either of them adds underneath can never move it again. Both cells
// are `align-top` for the same reason.
const BAR = { height: 8, radius: 999, labelW: 58, minW: 150 };

function ProgressCell({ pct }) {
  const value = Math.max(0, Math.min(100, Number(pct || 0)));
  const variant = progressVariant(value);

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: BAR.minW }}>
      {/* PROG's hover was singled out as worth keeping — "imi place mult si
          hooverul pe randurile din coloana PROG" — so this column gets one
          too, rather than the two behaving differently on the same gesture. */}
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            Produced on this drum: <b>{value}</b>% of its planned length
          </Tooltip>
        }
      >
        <div className="d-flex align-items-center gap-2">
          <div className="flex-grow-1">
            <ProgressBar
              now={value}
              variant={variant}
              style={{ height: BAR.height, borderRadius: BAR.radius }}
            />
          </div>
          <span
            className="fs-9 text-700"
            style={{ minWidth: BAR.labelW, textAlign: 'right' }}
          >
            {value}%
          </span>
        </div>
      </OverlayTrigger>
    </div>
  );
}
function ProgrammedCell({
  programmedLength,
  availableLength,
  // WHERE THOSE METRES GO. The bar could say how much of this drum is already
  // promised downstream but not onto which machine, so "6 760 m programmed"
  // gave nowhere to go and look. Several is normal — one drum can feed runs on
  // two machines at once — which is why this is a list and the chips group by
  // machine (helpers/phaseMachines.js).
  nextMachines = [],
  nextPhaseName = null
}) {
  const prog = Math.max(0, Number(programmedLength || 0));
  const avail = Math.max(0, Number(availableLength || 0));

  // raport din available (dacă avail=0, bar 0)
  const pct = avail > 0 ? Math.min(100, Math.round((prog / avail) * 100)) : 0;

  // culoare în funcție de “cât din available e deja programat”
  // 0..49: info, 50..89: warning, 90..100: success
  const variant = pct >= 90 ? 'success' : pct >= 50 ? 'warning' : 'info';

  const machines = normalizeMachines(nextMachines);
  const nextLabel = nextPhaseName
    ? PHASE_LABEL[nextPhaseName] || nextPhaseName
    : null;

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: BAR.minW }}>
      {/* The bar keeps its own tooltip; the chips carry theirs. Wrapping both
          in one OverlayTrigger would make hovering a machine explain the
          metres instead of the machine. */}
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            Programmed downstream (not done): <b>{prog.toFixed(2)}</b> m
            <br />
            Available now: <b>{avail.toFixed(2)}</b> m
          </Tooltip>
        }
      >
        <div className="d-flex align-items-center gap-2">
          <div className="flex-grow-1">
            <ProgressBar
              now={pct}
              variant={variant}
              style={{ height: BAR.height, borderRadius: BAR.radius }}
            />
          </div>
          <span
            className="fs-9 text-700"
            style={{ minWidth: BAR.labelW, textAlign: 'right' }}
          >
            {prog.toFixed(2)} m
          </span>
        </div>
      </OverlayTrigger>

      {machines.length > 0 && (
        <div className="d-flex align-items-center justify-content-end gap-1">
          <span className="text-body-tertiary" style={{ fontSize: 10 }}>
            →{nextLabel ? ` ${nextLabel}` : ''}
          </span>
          <MachineChips machines={machines} phaseLabel={nextLabel} max={2} />
        </div>
      )}
    </div>
  );
}

// Focused "Orders / Stock" view — dedicated to delivery/destination detail.
// Reuses the shared base columns (job / product / phase / status / available /
// produced — same cells, same relative order) and swaps the production-progress
// columns (token / progress / next / programmed) for Destination + Delivery
// (drums Ø×length / N×length coils) + Order + Customer.
// From the shared vocabulary — see helpers/orderDestination.js. Three screens
// answer "where is this cable going" and they used to disagree with each other
// about the colours as well as about stock being grey.
const DEST_BADGE = {
  ORDER: destinationBadge('ORDER').bg,
  STOCK: destinationBadge('STOCK').bg,
  MIXED: destinationBadge('MIXED').bg
};
const _colKey = c => c.accessorKey ?? c.id;
// Tighter widths for the focused view so the columns don't spread out with big
// gaps (they were reusing the full-view widths).
const _destColWidths = {
  jobId: '8%',
  productLabel: '10%',
  phase: '9%',
  status: '8%',
  availableLength: '8%',
  producedLength: '8%'
};
const _pickBaseCols = keys =>
  keys
    .map(k => {
      const c = projectBobinStateColumns.find(col => _colKey(col) === k);
      if (!c) return null;
      const w = _destColWidths[k];
      if (!w) return c;
      return {
        ...c,
        meta: {
          ...c.meta,
          headerProps: {
            ...c.meta?.headerProps,
            style: { ...(c.meta?.headerProps?.style || {}), width: w }
          }
        }
      };
    })
    .filter(Boolean);

// Destination = the STOCK / ORDER / MIXED label + every order code being worked.
const destinationCol = {
  accessorKey: 'destination',
  header: 'Destination',
  cell: ({ row: { original } }) => {
    const d = original.destination;
    if (!d) return '—';
    const orders = (d.orders || []).filter(o => o.orderCode);
    return (
      <div className="d-flex flex-column gap-1 align-items-start">
        <Badge
          bg={DEST_BADGE[d.kind] || 'secondary'}
          text={d.kind === 'MIXED' ? 'dark' : undefined}
        >
          {d.kind}
        </Badge>
        {/* Metres say how much is going to orders; this says how much of it is
            pinned to actual drums and coils. A product can be fully allocated
            and have promised nothing yet — the two are different states and
            the planner acts on them differently. */}
        {Number(d.reservedUnits) > 0 && (
          <Badge bg="info" variant="phoenix" className="fs-10">
            {d.reservedUnits} unit{d.reservedUnits === 1 ? '' : 's'} promised
          </Badge>
        )}
        {orders.map((o, i) => (
          <span key={i} className="fs-10">
            <span className="font-monospace text-muted">{o.orderCode}</span>
            {o.length ? (
              <span className="fw-semibold">
                {' · '}
                {Number(o.length).toLocaleString()} m
              </span>
            ) : null}
          </span>
        ))}
      </div>
    );
  },
  meta: {
    cellProps: ({ row }) => cellPropsForRow(row),
    headerProps: { style: { width: '18%' } }
  }
};

// A distinct, deterministic colour per client — solid background with white text,
// so the name stays readable on both light and dark themes.
const clientBadgeStyle = name => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return {
    backgroundColor: `hsl(${h}, 52%, 42%)`,
    color: '#fff',
    border: '1px solid rgba(0,0,0,0.15)'
  };
};

// Delivery: ORDER/MIXED rows show each order's delivery plan as its own block
// (order code badge + one line per drum/coil item); STOCK rows fall back to the
// physical drum/coil form of the drum.
const deliveryCol = {
  id: 'delivery',
  header: 'Delivery',
  cell: ({ row: { original: o } }) => {
    const d = o.destination;
    const orders = (d?.orders || []).filter(
      x => x.deliveryItems?.length || x.deliverySummary
    );
    if (d && (d.kind === 'ORDER' || d.kind === 'MIXED') && orders.length) {
      return (
        <div
          className="d-flex flex-column gap-2"
          style={{ whiteSpace: 'normal' }}
        >
          {orders.map((ord, i) => (
            <div key={i} className="lh-sm">
              {ord.deliveryItems?.length ? (
                ord.deliveryItems.map((it, j) => {
                  const q = Number(it.qtyPlanned) || 0;
                  const L = Number(it.lengthPerUnit) || 0;
                  /* What THIS product has promised of this item. The column
                     already says what the order wants; the promise is what
                     turns "wanted" into "spoken for", and it is the difference
                     between a drum a planner may still use and one that is
                     already somebody's. */
                  const res = Number(it.reserved) || 0;
                  const promised = res > 0 && (
                    <Badge
                      bg="info"
                      variant="phoenix"
                      className="fs-10 ms-2 align-middle"
                      title={`This product has promised ${res} of these to ${ord.orderCode ||
                        'this order'}`}
                    >
                      {res} promised
                    </Badge>
                  );
                  if (it.kind === 'DRUM') {
                    const spec = [
                      it.drumMaterial,
                      it.drumDiameter ? `Ø${it.drumDiameter}` : null
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <div key={j}>
                        <span className="fw-bold">{q} drums</span>
                        {spec ? (
                          <span className="text-muted"> · {spec}</span>
                        ) : null}{' '}
                        · {L.toLocaleString()} m each
                        {promised}
                      </div>
                    );
                  }
                  if (it.kind === 'COIL') {
                    return (
                      <div key={j}>
                        <span className="fw-bold">{q} coils</span> at{' '}
                        {L.toLocaleString()} m each
                        {promised}
                      </div>
                    );
                  }
                  return (
                    <div key={j}>
                      <span className="fw-bold">{q}</span> ×{' '}
                      {L.toLocaleString()} m
                      {promised}
                    </div>
                  );
                })
              ) : (
                <div>{ord.deliverySummary}</div>
              )}
            </div>
          ))}
        </div>
      );
    }
    const cc = Number(o.coilCount || 0);
    const cl = Number(o.coilLength || 0);
    if (cc > 0 && cl > 0) {
      return (
        <span>
          {cc} × {cl.toFixed(0)} m{' '}
          <span className="text-muted fs-10">coils</span>
        </span>
      );
    }
    const dia = o.drum_diameter;
    const mat = o.drum_material;
    const len = Number(o.producedLength || o.availableLength || o.plannedLength || 0);
    if (dia) {
      return (
        <span>
          <span className="text-muted fs-10">{mat ? `${mat} ` : ''}drum</span> Ø
          {dia}
          {len ? ` · ${len.toFixed(0)} m` : ''}
        </span>
      );
    }
    return len ? `${len.toFixed(0)} m` : '—';
  },
  meta: {
    cellProps: ({ row }) => cellPropsForRow(row),
    headerProps: { style: { width: '21%' } }
  }
};

// Customer — a badge per client, stacked when a job serves multiple orders.
const customerCol = {
  id: 'client',
  header: 'Customer',
  cell: ({ row: { original } }) => {
    const clients = original.destination?.clients || [];

    // A dash meant "for stock", and stopped there — the question it raises is
    // "could it go to anybody?", and answering it meant leaving for Order
    // Simulation, finding this product again in a table of the whole floor,
    // and coming back.
    //
    // The same button the launch wizard already carries: it asks the shared
    // allocation hook which open order lines this cable fits, and reserves
    // from the same write. Not a second mechanism — the same one, offered
    // where the drum is. Silent when there is no free length or nobody
    // waiting, so a stock drum nobody wants still just reads as a dash.
    if (!clients.length) {
      return (
        <div className="d-flex align-items-center gap-2">
          <span className="text-700">—</span>
          <ProductAllocationButton jobId={original.jobId} />
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-1 align-items-start">
        {clients.map((c, i) => (
          <Badge
            key={i}
            bg=""
            className="fw-normal text-wrap text-start"
            style={clientBadgeStyle(c)}
          >
            {c}
          </Badge>
        ))}
        {/* Partly promised is still partly free, so the offer stays. */}
        <ProductAllocationButton jobId={original.jobId} />
      </div>
    );
  },
  meta: {
    cellProps: ({ row }) => cellPropsForRow(row),
    headerProps: { style: { width: '10%' } }
  }
};

const destinationViewColumns = [
  ..._pickBaseCols([
    'jobId',
    'productLabel',
    'phase',
    'status',
    'availableLength',
    'producedLength'
  ]),
  destinationCol,
  deliveryCol,
  customerCol
];

// -------------------- component --------------------
// `fullPage` = rendered on its own dedicated route instead of embedded in
// Project Monitor. Only two things change: a taller page of rows, and no
// "Full view" button (it would link to the page you are already on).
const ProjectBobinState = ({ fullPage = false }) => {
  const { data, isLoading, error, refetch } = useBobinsState({ includePlanned: true });
  const [selectedPhase, setSelectedPhase] = useState('all');
  // Adding a phase to the SELECTION. The warehouse reception menu could always
  // do it to one received drum; this is the same dialog on the drums a planner
  // has in front of them, before any of them gets that far — and on several at
  // once, which is the whole reason this table has checkboxes.
  const [addPhaseTargets, setAddPhaseTargets] = useState(null);
  const [addPhaseFlash, setAddPhaseFlash] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all | completed | ongoing | inactive
  const [nextPhaseFilter, setNextPhaseFilter] = useState('all'); // all | programmed | partial | none
  const [searchTerm, setSearchTerm] = useState('');
  const [showPhantomModal, setShowPhantomModal] = useState(false);
  const [showPhantomView, setShowPhantomView] = useState(false);
  const [showPlanAheadView, setShowPlanAheadView] = useState(false);
  const [showDestinationView, setShowDestinationView] = useState(false);
  const [selectedPhantom, setSelectedPhantom] = useState(null);

  const navigate = useNavigate();

  const handleSearchInputChange = e => {
    setSearchTerm(e.target.value || '');
  };

  function matchSearch(row, searchTerm) {
    if (!searchTerm) return true;

    const q = searchTerm.toLowerCase().trim();
    if (!q) return true;

    return (
      String(row.productLabel || '')
        .toLowerCase()
        .includes(q) ||
      String(row.jobId || '')
        .toLowerCase()
        .includes(q) ||
      String(row.tokenId || '')
        .toLowerCase()
        .includes(q) ||
      String(row.phase || '')
        .toLowerCase()
        .includes(q) ||
      String(row.color || '')
        .toLowerCase()
        .includes(q)
    );
  }

  // A dropdown that gives no sign of what was picked is the whole complaint:
  // after choosing "On Going" the menu still just reads STATUS.
  const tick = (value, current, label) => (value === current ? `✓ ${label}` : `   ${label}`);

  const filterMenus = useMemo(() => {
    return [
      {
        label: 'STATUS',
        items: [
          { label: tick('all', statusFilter, 'All'), onClick: () => setStatusFilter('all') },
          { label: tick('completed', statusFilter, 'Completed'), onClick: () => setStatusFilter('completed') },
          { label: tick('ongoing', statusFilter, 'On Going'), onClick: () => setStatusFilter('ongoing') },
          { label: tick('inactive', statusFilter, 'Inactive'), onClick: () => setStatusFilter('inactive') }
        ]
      },
      {
        label: 'Next Phase',
        items: [
          { label: tick('all', nextPhaseFilter, 'All'), onClick: () => setNextPhaseFilter('all') },
          {
            label: tick('programmed', nextPhaseFilter, 'Programmed'),
            onClick: () => setNextPhaseFilter('programmed')
          },
          {
            label: tick('partial', nextPhaseFilter, 'Partially Programmed'),
            onClick: () => setNextPhaseFilter('partial')
          },
          { label: tick('none', nextPhaseFilter, 'Not Programmed'), onClick: () => setNextPhaseFilter('none') }
        ]
      }
    ];
    // The menus are rebuilt when a filter changes so the tick follows it — the
    // old `[]` deps froze them at "nothing selected" for the life of the page.
  }, [statusFilter, nextPhaseFilter]);

  // Memoised because five useMemos below depend on it, and `|| []` is a new
  // array identity on every render — so each of them recomputed on every poll
  // (this query refetches every 5 s) instead of when the data actually changed.
  const allTokens = useMemo(() => data?.tokens || [], [data]);

  // separate real / phantom / planned tokens.
  //
  // A planned token is a PENDING drum: launched, queued on a machine, not made
  // yet. EVERY one of them belongs behind the "Plan ahead" button and nowhere
  // else — the main list is what physically exists on the floor, and a drum that
  // has not been made does not belong in it (user, 2026-10-12).
  //
  // They still come in two kinds, and `isTerminalPlan` (set by /physical-tokens)
  // keeps them apart INSIDE that view:
  //
  //   · feeds a downstream phase → there is a next phase to schedule early,
  //     which is what the view was built for
  //   · nothing follows it — the drum IS the product. It used to fail the
  //     endpoint's `nextPhaseName` gate and so appeared on NO view at all. A
  //     single-phase product (FY: one conductor_isolation phase) is entirely
  //     made of these, so launching one showed up nowhere until the extruder
  //     started. It is listed here now, badged QUEUED · NOT MADE YET, with no
  //     next phase to plan.
  const realTokens = useMemo(
    () => allTokens.filter(t => !t.isPhantom && !t.isPlanned),
    [allTokens]
  );
  const phantomTokens = useMemo(() => allTokens.filter(t => t.isPhantom), [allTokens]);
  const plannedTokens = useMemo(() => allTokens.filter(t => t.isPlanned), [allTokens]);
  // The terminal subset, for the count that points somebody at the view.
  const queuedTokens = useMemo(
    () => plannedTokens.filter(t => t.isTerminalPlan),
    [plannedTokens]
  );

  const tokens = showDestinationView
    ? realTokens
    : showPlanAheadView
    ? plannedTokens
    : showPhantomView
    ? phantomTokens
    : realTokens;

  // One bobbin, two products — a merge-execution or a partial fork leaves the
  // SAME tokenId under both jobs. Built from every token the endpoint returned,
  // so a partner hidden behind the current filters still counts.
  const sharedIndex = useMemo(() => buildSharedTokenIndex(allTokens), [allTokens]);

  const sharedCount = useMemo(() => {
    const ids = new Set();
    for (const t of tokens) if (sharedIndex.has(String(t.tokenId))) ids.add(String(t.tokenId));
    return ids.size;
  }, [tokens, sharedIndex]);

  const tabItems = useMemo(() => buildPhaseTabs(tokens), [tokens]);

  const rows = useMemo(() => {
    // 1) phase filter (existing)
    const phaseFiltered =
      selectedPhase === 'all'
        ? tokens
        : tokens.filter(t => t.phase === selectedPhase);

    // 2) status / nextPhase filters (new)
    const otherFiltered = phaseFiltered.filter(t => {
      return (
        matchStatusFilter(t, statusFilter) &&
        matchNextPhaseFilter(t, nextPhaseFilter) &&
        matchSearch(t, searchTerm)
      );
    });

    // 3) grouping + computed fields
    return buildRowsWithGrouping(otherFiltered, 'all', sharedIndex); // deja filtrat pe phase
  }, [tokens, selectedPhase, statusFilter, nextPhaseFilter, searchTerm, sharedIndex]);

  // Orders/Stock view swaps to a focused, delivery-oriented column set (shared
  // columns keep their relative order); default view is unchanged.
  const tableColumns = useMemo(
    () => (showDestinationView ? destinationViewColumns : projectBobinStateColumns),
    [showDestinationView]
  );

  const table = useAdvanceTable({
    data: rows,
    columns: tableColumns,
    pageSize: fullPage ? FULL_TABLE_PAGE_SIZE.tokenState : 10,
    pagination: true,
    sortable: true,
    selection: true,
    autoResetPageIndex: false
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedOriginals = selectedRows.map(r => r.original);
  const selectedCount = selectedOriginals.length;

  const selectedTokenIds = useMemo(() => {
    const set = new Set();
    for (const r of selectedOriginals) if (r?.tokenId) set.add(r.tokenId);
    return Array.from(set);
  }, [selectedOriginals]);

  // Queued TERMINAL drums are listed here so the planner can see the launch
  // landed. Nothing follows them — the Next column says so with a red cross — so
  // "Plan next phase" has nothing to plan and would open a wizard where every
  // length clamps to zero.
  //
  // Transform / Fork is the opposite: it reads a queued drum on purpose
  // (`isPending` → merge-execution for a partial, convert-product for a total),
  // and converting a launched product in place is how a job launched as the
  // wrong colour gets repaired. So that one stays live.
  const selectionHasQueued = useMemo(
    () => selectedOriginals.some(r => r?.isPlanned && r?.isTerminalPlan),
    [selectedOriginals]
  );

  const handlePlanNextPhase = () => {
    if (!selectedTokenIds.length) return;
    if (selectionHasQueued) return;

    navigate('/apps/project-management/plan-next-phase-wizard', {
      state: {
        from: 'ProjectBobinState',
        tokenIds: selectedTokenIds,
        rows: selectedOriginals
      }
    });

    // ✅ optional, same style as your other table
    table.resetRowSelection();
  };

  // Transform / Fork requires a single source product + single phase (the branch).
  const transformOk = useMemo(() => {
    if (!selectedOriginals.length) return false;
    const jobs = new Set(selectedOriginals.map(r => String(r.jobId)));
    const phases = new Set(selectedOriginals.map(r => String(r.phase)));
    return jobs.size === 1 && phases.size === 1;
  }, [selectedOriginals]);

  // Combine is the opposite of Transform: it wants drums of TWO OR MORE
  // products, because what it unifies is the products, not the drums. Whether
  // they are really the same cable is not decided here — the endpoint's dry run
  // answers that against the documents (name, conductors, section, the
  // mixed-section flag, the green-yellow section, the colour, the Standard and
  // the phase chain), and the modal shows the answer before anything is written.
  const combineJobIds = useMemo(
    () => [...new Set(selectedOriginals.map(r => String(r.jobId)).filter(Boolean))],
    [selectedOriginals]
  );
  const combineOk = combineJobIds.length >= 2;
  const [combining, setCombining] = useState(false);

  // The way back. Offered on a product that HAS absorbed something, which the
  // row knows because the endpoint stamps `_combined` on the survivor.
  const combinedJobId = useMemo(() => {
    const withLedger = selectedOriginals.filter(r => Number(r?.combinedCount) > 0);
    const jobs = new Set(withLedger.map(r => String(r.jobId)));
    return jobs.size === 1 ? [...jobs][0] : null;
  }, [selectedOriginals]);
  const [uncombining, setUncombining] = useState(false);

  /**
   * Declare the selected drums empty.
   *
   * A write-off, not a correction: the metres are not moved anywhere, they are
   * declared gone — so the figure is shown before it is asked for, and the
   * server records who did it and why on each drum.
   *
   * Grouped by product because the endpoint is per job, and a selection in this
   * table crosses products freely.
   */
  const handleCloseRemainder = async () => {
    const byJob = new Map();
    for (const row of selectedOriginals) {
      if (!row?.jobId || !row?.tokenId) continue;
      const id = String(row.jobId);
      if (!byJob.has(id)) byJob.set(id, { label: row.productLabel, tokenIds: [] });
      byJob.get(id).tokenIds.push(String(row.tokenId));
    }
    if (!byJob.size) return;

    // Asked first, with the figure: the dry run is what knows how much each drum
    // still says it holds, and writing off 8 390 m is a different act from
    // writing off 15.
    let total = 0;
    const lines = [];
    const refusals = [];
    for (const [jobId, { label, tokenIds }] of byJob) {
      try {
        const res = await redisFetch(`/api/redis/jobs/${jobId}/close-remainder`, {
          method: 'POST',
          body: JSON.stringify({ tokenIds, dryRun: true })
        });
        const data = await res.json();
        total += Number(data?.total) || 0;
        for (const r of data?.rows || []) {
          lines.push(`${label || jobId} ${r.color || '-'} ${r.tokenId}: ${exactLength(r.wroteOff)} m`);
        }
        for (const r of data?.refused || []) refusals.push(`${r.tokenId}: ${r.why}`);
      } catch (e) {
        refusals.push(`${label || jobId}: ${e.message || e}`);
      }
    }

    if (!lines.length) {
      window.alert(
        refusals.length
          ? `Nothing to write off.\n\n${refusals.join('\n')}`
          : 'Nothing to write off — these drums already hold nothing.'
      );
      return;
    }

    const reason = window.prompt(
      `Write off ${exactLength(total)} m across ${lines.length} drum(s)?\n\n` +
        `${lines.join('\n')}\n\n` +
        (refusals.length ? `Skipped:\n${refusals.join('\n')}\n\n` : '') +
        'The metres are declared gone, not moved. Why? (goes on the drum and the timeline)',
      'tail too short to use'
    );
    if (reason === null) return;

    for (const [jobId, { tokenIds }] of byJob) {
      try {
        await redisFetch(`/api/redis/jobs/${jobId}/close-remainder`, {
          method: 'POST',
          body: JSON.stringify({ tokenIds, reason })
        });
      } catch (e) {
        window.alert(`${jobId}: ${e.message || e}`);
      }
    }

    refetch?.();
    table.resetRowSelection?.();
  };

  const handleTransform = () => {
    if (!transformOk) return;
    navigate('/apps/project-management/transform-wizard', {
      state: {
        sourceJobId: String(selectedOriginals[0].jobId),
        tokenIds: selectedTokenIds,
        rows: selectedOriginals,
        // Come back to wherever the drums were actually selected — landing on
        // Project Monitor after starting from the full view loses the context
        // (and the scroll position) the user was working in.
        returnTo: fullPage
          ? FULL_TABLE_ROUTES.tokenState
          : FULL_TABLE_PARENTS.projectMonitor
      }
    });
    table.resetRowSelection();
  };

  if (isLoading) return <div>Loading .. . . </div>;

  if (error) {
    return (
      <div className="p-4">
        <h4>Error</h4>
        <pre className="text-danger">{String(error?.message || error)}</pre>
      </div>
    );
  }

  return (
    <div className="mb-9 mt-6">
      <div className="d-flex align-items-center gap-3 mb-4">
        <h2 className="mb-0">Project Token State</h2>

        <Button
          variant={showPhantomView ? 'info' : 'outline-info'}
          size="sm"
          onClick={() => {
            setShowPhantomView(v => !v);
            setShowPlanAheadView(false);
            setShowDestinationView(false);
          }}
        >
          {showPhantomView
            ? `Phantoms (${phantomTokens.length})`
            : `Show Phantoms (${phantomTokens.length})`}
        </Button>

        <Button
          variant={showPlanAheadView ? 'warning' : 'outline-warning'}
          size="sm"
          onClick={() => {
            setShowPlanAheadView(v => !v);
            setShowPhantomView(false);
            setShowDestinationView(false);
          }}
          title={
            'Every drum that is launched and queued but not made yet. Schedule the next phase of one ahead of production' +
            (queuedTokens.length
              ? ` — ${queuedTokens.length} of them are final drums with no next phase, listed as QUEUED · NOT MADE YET`
              : '')
          }
        >
          Plan ahead ({plannedTokens.length})
        </Button>

        <Button
          variant={showDestinationView ? 'primary' : 'outline-primary'}
          size="sm"
          onClick={() => {
            setShowDestinationView(v => !v);
            setShowPhantomView(false);
            setShowPlanAheadView(false);
          }}
          title="Show which products are made for orders vs for stock (adds destination / order / customer columns)"
        >
          Orders / Stock
        </Button>

        {/* Not a filter — a signpost. The main list is what physically exists,
            so a drum that has only been launched is not in it; without this, the
            way to find out a launch landed is to guess that Plan ahead is where
            it went. */}
        {queuedTokens.length > 0 && !showPlanAheadView && (
          <Badge
            bg="secondary"
            variant="phoenix"
            className="fs-10"
            title="Launched and queued on a machine, nothing produced yet. They are not in this list — press Plan ahead."
          >
            {queuedTokens.length} queued, not made yet → Plan ahead
          </Badge>
        )}

        {sharedCount > 0 && (
          <Badge
            bg="warning"
            variant="phoenix"
            className="fs-10"
            title="A merge-execution or a partial fork puts several products on one physical bobbin. Those drums are listed under each product — the dashed token is the same object, not another drum."
          >
            ⇄ {sharedCount} shared bobbin{sharedCount === 1 ? '' : 's'}
          </Badge>
        )}

        {!fullPage && (
          <TableExpandButton
            to={FULL_TABLE_ROUTES.tokenState}
            className="ms-auto"
          />
        )}
      </div>

      {/* Everything currently narrowing the table, in one place. A filtered
          view with nothing on screen saying so is the worst kind: an empty
          result reads as "there is nothing" rather than "you are looking
          through a slot". */}
      {(() => {
        const chips = activeFilters({
          showPhantomView,
          showPlanAheadView,
          showDestinationView,
          statusFilter,
          nextPhaseFilter,
          selectedPhase,
          search: searchTerm
        });
        if (!chips.length) return null;

        const clearOne = {
          showPhantomView: () => setShowPhantomView(false),
          showPlanAheadView: () => setShowPlanAheadView(false),
          showDestinationView: () => setShowDestinationView(false),
          statusFilter: () => setStatusFilter(CLEARED.statusFilter),
          nextPhaseFilter: () => setNextPhaseFilter(CLEARED.nextPhaseFilter),
          selectedPhase: () => setSelectedPhase(CLEARED.selectedPhase),
          search: () => setSearchTerm('')
        };

        return (
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <span className="fs-10 text-700">Filtered by:</span>
            {chips.map(c => (
              <Badge
                key={c.key}
                variant="phoenix"
                bg="info"
                className="fs-10"
                title={`Remove this filter`}
              >
                {c.group === 'view' ? c.label : `${c.group}: ${c.label}`}
                <Button
                  variant="link"
                  className="p-0 ms-2 fs-10 align-baseline text-decoration-none"
                  onClick={clearOne[c.key]}
                >
                  ×
                </Button>
              </Badge>
            ))}
            <Button
              variant="link"
              size="sm"
              className="fs-10 p-0"
              onClick={() => {
                setShowPhantomView(false);
                setShowPlanAheadView(false);
                setShowDestinationView(false);
                setStatusFilter(CLEARED.statusFilter);
                setNextPhaseFilter(CLEARED.nextPhaseFilter);
                setSelectedPhase(CLEARED.selectedPhase);
                setSearchTerm('');
              }}
            >
              clear all
            </Button>
          </div>
        );
      })()}

      {/* The outcome of an add-phase. It reopens jobs and moves drums between
          two services, so "nothing is scheduled yet" is the part that otherwise
          gets missed — and when the warehouse keeps one, that has to be said. */}
      {addPhaseFlash && (
        <Badge
          bg={addPhaseFlash.variant === 'warning' ? 'warning' : 'success'}
          className="d-block text-start text-wrap fs-9 p-2 mb-2"
          role="button"
          title="Dismiss"
          onClick={() => setAddPhaseFlash(null)}
        >
          {addPhaseFlash.message}
        </Badge>
      )}

      <FilterTab
        tabItems={tabItems}
        activeKey={selectedPhase}
        onSelect={setSelectedPhase}
        className="mb-2"
      />

      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search..."
              onChange={handleSearchInputChange}
            />
            <div className="scrollbar overflow-hidden-y">
              <FilterButtonGroup menus={filterMenus} />
            </div>
            <div className="ms-xxl-auto">
              {selectedCount > 0 && (
                <Button
                  className="me-4"
                  variant="success"
                  disabled={selectionHasQueued}
                  title={
                    selectionHasQueued
                      ? 'One of the selected drums is queued and not made yet — there is nothing after it to plan'
                      : undefined
                  }
                  onClick={handlePlanNextPhase}
                >
                  Plan next phase ({selectedCount})
                </Button>
              )}
              {selectedCount > 0 && (
                <Button
                  className="me-4"
                  variant="phoenix-warning"
                  disabled={!transformOk}
                  title={
                    transformOk
                      ? 'Re-plan these drums into a different product — in place for a whole product (which is how a launch with the wrong colour is repaired), as a fork for part of one'
                      : 'Select drums of a single product + single phase'
                  }
                  onClick={handleTransform}
                >
                  Transform / Fork ({selectedCount})
                </Button>
              )}
              {selectedCount > 0 && (
                <Button
                  className="me-4"
                  variant="phoenix-primary"
                  disabled={!combineOk}
                  title={
                    combineOk
                      ? 'Make these launches ONE product: the lot adds up, every run and drum keeps its id, and only one job code survives (the oldest). Nothing is written until you have read the report'
                      : 'Select drums of two or more products — this unifies the products, not the drums'
                  }
                  onClick={() => setCombining(true)}
                >
                  Combine products ({combineJobIds.length})
                </Button>
              )}
              {combinedJobId && (
                <Button
                  className="me-4"
                  variant="phoenix-warning"
                  title="Take the last combine back apart — the absorbed product was archived whole, so it is restored exactly as it was"
                  onClick={() => setUncombining(true)}
                >
                  Undo combine
                </Button>
              )}
              {selectedCount > 0 && (
                <Button
                  className="me-4"
                  variant="phoenix-info"
                  title="Send these drums on to coiling, rewinding or an outer sheath — the same thing the warehouse reception menu does, without waiting for them to get there"
                  onClick={() =>
                    setAddPhaseTargets(
                      selectedRows.map(r => ({
                        jobId: r.original.jobId,
                        tokenId: r.original.tokenId,
                        phase: r.original.phase,
                        productLabel: r.original.productLabel,
                        cableName: r.original.productLabel,
                        lengthMeters: r.original.availableLength
                      }))
                    )
                  }
                >
                  Add phase ({selectedCount})
                </Button>
              )}
              {/* The tails. A rope is only as long as its shortest core, so every
                  run leaves a few metres on one drum that nothing will ever use:
                  the next rope needs every colour at once and one of them is at
                  zero. The worker writes those off by itself under 300 m, and
                  anything above that — a 463 m tail, or a drum left reading
                  thousands after a consumption went wrong — sits in this table
                  for good. This says so out loud. */}
              {selectedCount > 0 && (
                <Button
                  className="me-4"
                  variant="phoenix-secondary"
                  title="Declare these drums empty and take them out of this table. A write-off: the metres are not moved anywhere, so it is recorded on the drum and on the timeline"
                  onClick={handleCloseRemainder}
                >
                  Close remainder ({selectedCount})
                </Button>
              )}
              <Button variant="link" className="text-body me-4 px-0">
                <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                Export
              </Button>
              <Button variant="primary" onClick={() => setShowPhantomModal(true)}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Phantom
              </Button>
            </div>
          </div>
        </div>

        <div
          className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1"
          onClick={e => {
            const el = e.target.closest('[data-phantom-id]');
            if (!el) return;
            const pid = el.getAttribute('data-phantom-id');
            if (!pid) return;
            const ph = phantomTokens.find(t => t.tokenId === pid);
            if (ph?.phantomData) {
              setSelectedPhantom(ph.phantomData);
            }
          }}
        >
          <ProjectBobinStateTable />
        </div>
      </AdvanceTableProvider>

      {/* One definition of "add a phase", shared with the warehouse reception
          menu, the yard in Factory 3D and project-details. Here it takes the
          whole selection and groups it by job, because `add-phase` is per job
          and a selection can span products. */}
      <AddPhaseDialog
        targets={addPhaseTargets}
        onHide={() => setAddPhaseTargets(null)}
        onDone={res => {
          setAddPhaseFlash(res);
          // The drums have moved: a job reopened, their phase changed, the
          // warehouse may have let one go. Nothing on this page is right until
          // it is re-read.
          refetch?.();
          table.resetRowSelection?.();
        }}
      />

      <AddPhantomTokenModal
        show={showPhantomModal}
        onHide={() => setShowPhantomModal(false)}
      />

      <PhantomTokenDetailModal
        show={!!selectedPhantom}
        onHide={() => setSelectedPhantom(null)}
        phantom={selectedPhantom}
      />

      {/* Both directions, one component. The report it opens with IS the
          endpoint's dry run, so what the planner reads is what the write would
          produce rather than a second account of it. */}
      <CombineProductsModal
        show={combining}
        onHide={() => setCombining(false)}
        mode="combine"
        jobIds={combineJobIds}
        onDone={() => {
          // One product where there were two: every row on this page is stale.
          refetch?.();
          table.resetRowSelection?.();
        }}
      />

      <CombineProductsModal
        show={uncombining}
        onHide={() => setUncombining(false)}
        mode="undo"
        jobId={combinedJobId}
        onDone={() => {
          refetch?.();
          table.resetRowSelection?.();
        }}
      />
    </div>
  );
};

export default ProjectBobinState;
